import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const currencies = [
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'SEK', symbol: 'kr', name: 'Swedish Krona' },
  { code: 'NOK', symbol: 'kr', name: 'Norwegian Krone' },
  { code: 'DKK', symbol: 'kr', name: 'Danish Krone' },
  { code: 'CHF', symbol: 'Fr', name: 'Swiss Franc' },
  { code: 'CAD', symbol: '$', name: 'Canadian Dollar' },
  { code: 'AUD', symbol: '$', name: 'Australian Dollar' },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
];

const jobTags = [
  { value: 'it_developer', label: 'I.T Developer', icon: '💻' },
  { value: 'cybersecurity', label: 'Cybersecurity', icon: '🔒' },
  { value: 'engineer', label: 'Engineer', icon: '⚙️' },
  { value: 'food_and_service', label: 'Food & Service', icon: '🍽️' },
  { value: 'sales', label: 'Sales', icon: '📈' },
  { value: 'marketing', label: 'Marketing', icon: '📣' },
  { value: 'administrative', label: 'Administrative', icon: '📋' },
  { value: 'accounting', label: 'Accounting', icon: '💰' },
  { value: 'public_relations', label: 'Public Relations', icon: '🤝' },
  { value: 'healthcare', label: 'Healthcare', icon: '🏥' },
  { value: 'agriculture', label: 'Agriculture', icon: '🌾' },
  { value: 'executive', label: 'Executive', icon: '👔' },
];

const styles = {
  global: `
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }
    
    body {
      font-family: 'Outfit', -apple-system, BlinkMacSystemFont, sans-serif;
      background: linear-gradient(135deg, #e8f0e9 0%, #d4e5d8 50%, #c5dde0 100%);
      min-height: 100vh;
      color: #3d4f4f;
    }
    
    @keyframes fadeIn {
      from { opacity: 0; transform: translateY(10px); }
      to { opacity: 1; transform: translateY(0); }
    }
    
    @keyframes float {
      0%, 100% { transform: translateY(0px); }
      50% { transform: translateY(-8px); }
    }
    
    button:hover {
      transform: translateY(-2px);
    }
    button:active {
      transform: translateY(0);
    }
    
    input::placeholder, textarea::placeholder {
      color: #9ab0a5;
    }
    
    input:focus, textarea:focus, select:focus {
      outline: none;
      border-color: #7c9885;
      background: white;
      box-shadow: 0 0 0 4px rgba(124, 152, 133, 0.1);
    }
  `,
  
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    position: 'relative',
    overflow: 'hidden',
  },
  
  bgCircle1: {
    position: 'absolute',
    width: '400px',
    height: '400px',
    borderRadius: '50%',
    background: 'rgba(124, 152, 133, 0.15)',
    top: '-100px',
    right: '-100px',
    animation: 'float 8s ease-in-out infinite',
  },
  
  bgCircle2: {
    position: 'absolute',
    width: '300px',
    height: '300px',
    borderRadius: '50%',
    background: 'rgba(147, 180, 186, 0.12)',
    bottom: '-80px',
    left: '-80px',
    animation: 'float 10s ease-in-out infinite 1s',
  },
  
  bgCircle3: {
    position: 'absolute',
    width: '150px',
    height: '150px',
    borderRadius: '50%',
    background: 'rgba(200, 180, 160, 0.1)',
    top: '40%',
    left: '10%',
    animation: 'float 6s ease-in-out infinite 0.5s',
  },
  
  mainCard: {
    background: 'rgba(255, 255, 255, 0.85)',
    backdropFilter: 'blur(20px)',
    borderRadius: '24px',
    padding: '32px',
    width: '100%',
    maxWidth: '1100px',
    maxHeight: '90vh',
    overflowY: 'auto',
    boxShadow: '0 8px 32px rgba(61, 79, 79, 0.08), 0 2px 8px rgba(61, 79, 79, 0.04)',
    border: '1px solid rgba(255, 255, 255, 0.6)',
    animation: 'fadeIn 0.6s ease-out',
    position: 'relative',
    zIndex: 1,
  },
  
  header: {
    textAlign: 'center',
    marginBottom: '24px',
  },
  
  logoText: {
    fontSize: '28px',
    fontWeight: '600',
    color: '#3d4f4f',
    letterSpacing: '-0.5px',
  },
  
  subtitle: {
    fontSize: '18px',
    color: '#6b8080',
    marginTop: '4px',
  },
  
  contentWrapper: {
    display: 'flex',
    gap: '24px',
  },
  
  formSection: {
    flex: 1,
    minWidth: 0,
  },
  
  previewSection: {
    flex: 1,
    minWidth: 0,
  },
  
  sectionTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#3d4f4f',
    marginBottom: '16px',
    paddingBottom: '8px',
    borderBottom: '2px solid rgba(124, 152, 133, 0.2)',
  },
  
  formGroup: {
    marginBottom: '16px',
  },
  
  label: {
    display: 'block',
    fontSize: '16px',
    fontWeight: '500',
    color: '#5a7070',
    marginBottom: '6px',
  },
  
  input: {
    width: '100%',
    padding: '12px 14px',
    border: '2px solid rgba(124, 152, 133, 0.2)',
    borderRadius: '10px',
    fontSize: '16px',
    color: '#3d4f4f',
    background: 'rgba(255, 255, 255, 0.8)',
    transition: 'all 0.3s ease',
    fontFamily: 'inherit',
  },
  
  select: {
    width: '100%',
    padding: '12px 14px',
    border: '2px solid rgba(124, 152, 133, 0.2)',
    borderRadius: '10px',
    fontSize: '16px',
    color: '#3d4f4f',
    background: 'rgba(255, 255, 255, 0.8)',
    transition: 'all 0.3s ease',
    fontFamily: 'inherit',
    cursor: 'pointer',
  },
  
  textarea: {
    width: '100%',
    padding: '12px 14px',
    border: '2px solid rgba(124, 152, 133, 0.2)',
    borderRadius: '10px',
    fontSize: '16px',
    color: '#3d4f4f',
    background: 'rgba(255, 255, 255, 0.8)',
    transition: 'all 0.3s ease',
    fontFamily: 'inherit',
    resize: 'vertical',
    minHeight: '120px',
  },
  
  row: {
    display: 'flex',
    gap: '12px',
  },
  
  halfWidth: {
    flex: 1,
  },
  
  // Tag selection styles
  tagGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '10px',
  },
  
  tagButton: {
    padding: '12px 8px',
    border: '2px solid rgba(124, 152, 133, 0.2)',
    borderRadius: '10px',
    background: 'rgba(255, 255, 255, 0.8)',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    fontFamily: 'inherit',
    textAlign: 'center',
  },
  
  tagButtonSelected: {
    borderColor: '#7c9885',
    background: 'rgba(124, 152, 133, 0.15)',
    boxShadow: '0 0 0 4px rgba(124, 152, 133, 0.1)',
  },
  
  tagIcon: {
    fontSize: '24px',
    display: 'block',
    marginBottom: '4px',
  },
  
  tagLabel: {
    fontSize: '12px',
    fontWeight: '500',
    color: '#3d4f4f',
  },
  
  // Radio button styles
  radioGroup: {
    display: 'flex',
    gap: '12px',
  },
  
  radioOption: {
    flex: 1,
    position: 'relative',
  },
  
  radioInput: {
    position: 'absolute',
    opacity: 0,
    width: '100%',
    height: '100%',
    cursor: 'pointer',
    zIndex: 2,
  },
  
  radioLabel: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '16px 12px',
    border: '2px solid rgba(124, 152, 133, 0.2)',
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    background: 'rgba(255, 255, 255, 0.8)',
  },
  
  radioLabelSelected: {
    borderColor: '#7c9885',
    background: 'rgba(124, 152, 133, 0.1)',
    boxShadow: '0 0 0 4px rgba(124, 152, 133, 0.1)',
  },
  
  radioIcon: {
    fontSize: '24px',
    marginBottom: '8px',
  },
  
  radioText: {
    fontSize: '15px',
    fontWeight: '500',
    color: '#3d4f4f',
    textAlign: 'center',
  },
  
  charCount: {
    fontSize: '14px',
    color: '#9ab0a5',
    textAlign: 'right',
    marginTop: '4px',
  },
  
  charCountWarning: {
    color: '#b47878',
  },
  
  // Preview styles
  jobPreview: {
    background: 'white',
    borderRadius: '12px',
    padding: '24px',
    minHeight: '500px',
    boxShadow: '0 2px 8px rgba(61, 79, 79, 0.06)',
    border: '1px solid rgba(124, 152, 133, 0.1)',
  },
  
  jobHeader: {
    borderBottom: '2px solid #7c9885',
    paddingBottom: '16px',
    marginBottom: '16px',
  },
  
  jobCompany: {
    fontSize: '16px',
    fontWeight: '500',
    color: '#7c9885',
    marginBottom: '4px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  
  jobTitle: {
    fontSize: '28px',
    fontWeight: '600',
    color: '#3d4f4f',
    marginBottom: '12px',
  },
  
  jobMeta: {
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  
  jobMetaItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '15px',
    color: '#6b8080',
  },
  
  jobMetaIcon: {
    fontSize: '18px',
  },
  
  jobSection: {
    marginBottom: '20px',
  },
  
  jobSectionTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#7c9885',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '10px',
    paddingBottom: '4px',
    borderBottom: '1px solid rgba(124, 152, 133, 0.2)',
  },
  
  jobDescription: {
    fontSize: '16px',
    color: '#5a7070',
    lineHeight: '1.7',
    whiteSpace: 'pre-wrap',
  },
  
  jobSalary: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#3d4f4f',
  },
  
  jobEmpty: {
    color: '#bdc8c4',
    fontStyle: 'italic',
    fontSize: '15px',
  },
  
  employmentBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '6px 14px',
    borderRadius: '20px',
    fontSize: '14px',
    fontWeight: '500',
  },
  
  fullTimeBadge: {
    background: 'rgba(124, 152, 133, 0.15)',
    color: '#5a7a65',
  },
  
  partTimeBadge: {
    background: 'rgba(147, 180, 186, 0.2)',
    color: '#4a7a85',
  },
  
  tagBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 14px',
    borderRadius: '20px',
    fontSize: '14px',
    fontWeight: '500',
    background: 'rgba(200, 180, 160, 0.2)',
    color: '#6a5a4a',
  },
  
  // Button styles
  buttonContainer: {
    marginTop: '20px',
    display: 'flex',
    gap: '12px',
  },
  
  button: {
    padding: '14px 24px',
    border: 'none',
    borderRadius: '12px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    fontFamily: 'inherit',
  },
  
  buttonPrimary: {
    background: 'linear-gradient(135deg, #7c9885 0%, #6a8a74 100%)',
    color: 'white',
    boxShadow: '0 4px 12px rgba(124, 152, 133, 0.35)',
  },
  
  buttonSecondary: {
    background: 'rgba(124, 152, 133, 0.1)',
    color: '#5a7070',
  },

  // Upload section styles
  uploadSection: {
    marginBottom: '24px',
    padding: '24px',
    background: 'linear-gradient(135deg, rgba(124, 152, 133, 0.08) 0%, rgba(147, 180, 186, 0.08) 100%)',
    borderRadius: '16px',
    border: '2px dashed rgba(124, 152, 133, 0.3)',
    textAlign: 'center',
  },

  uploadSectionActive: {
    borderColor: '#7c9885',
    background: 'rgba(124, 152, 133, 0.15)',
  },

  uploadIcon: {
    fontSize: '48px',
    marginBottom: '12px',
  },

  uploadTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#3d4f4f',
    marginBottom: '8px',
  },

  uploadSubtitle: {
    fontSize: '14px',
    color: '#6b8080',
    marginBottom: '16px',
  },

  uploadButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 24px',
    background: 'linear-gradient(135deg, #7c9885 0%, #5a7a65 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    fontFamily: 'inherit',
    boxShadow: '0 4px 12px rgba(124, 152, 133, 0.35)',
  },

  uploadInput: {
    display: 'none',
  },

  uploadFormats: {
    fontSize: '12px',
    color: '#9ab0a5',
    marginTop: '12px',
  },

  uploadProgress: {
    marginTop: '16px',
    padding: '12px',
    background: 'rgba(255, 255, 255, 0.8)',
    borderRadius: '10px',
  },

  progressBar: {
    height: '8px',
    background: 'rgba(124, 152, 133, 0.2)',
    borderRadius: '4px',
    overflow: 'hidden',
    marginBottom: '8px',
  },

  progressFill: {
    height: '100%',
    background: 'linear-gradient(135deg, #7c9885 0%, #5a7a65 100%)',
    borderRadius: '4px',
    transition: 'width 0.3s ease',
  },

  progressText: {
    fontSize: '13px',
    color: '#5a7070',
    textAlign: 'center',
  },

  uploadedFile: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '16px',
    background: 'rgba(124, 152, 133, 0.1)',
    borderRadius: '12px',
    marginTop: '16px',
  },

  uploadedFileIcon: {
    fontSize: '32px',
  },

  uploadedFileInfo: {
    flex: 1,
  },

  uploadedFileName: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#3d4f4f',
    marginBottom: '2px',
  },

  uploadedFileMeta: {
    fontSize: '12px',
    color: '#6b8080',
  },

  uploadedFileActions: {
    display: 'flex',
    gap: '8px',
  },

  deleteButton: {
    padding: '8px 16px',
    background: 'rgba(180, 120, 120, 0.15)',
    color: '#8a5a5a',
    border: 'none',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'all 0.2s ease',
  },

  // Modal styles
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    animation: 'fadeIn 0.2s ease-out',
  },

  modalContent: {
    background: 'white',
    borderRadius: '16px',
    padding: '32px',
    maxWidth: '400px',
    width: '90%',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
    textAlign: 'center',
  },

  modalIcon: {
    fontSize: '48px',
    marginBottom: '16px',
  },

  modalTitle: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#3d4f4f',
    marginBottom: '12px',
  },

  modalText: {
    fontSize: '15px',
    color: '#6b8080',
    marginBottom: '24px',
    lineHeight: '1.5',
  },

  modalButtons: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'center',
  },

  modalButtonYes: {
    padding: '12px 32px',
    background: 'linear-gradient(135deg, #b47878 0%, #8a5a5a 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'all 0.2s ease',
  },

  modalButtonNo: {
    padding: '12px 32px',
    background: 'rgba(124, 152, 133, 0.1)',
    color: '#5a7070',
    border: 'none',
    borderRadius: '10px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'all 0.2s ease',
  },

  newListingButton: {
    padding: '12px 20px',
    background: 'rgba(180, 120, 120, 0.1)',
    color: '#8a5a5a',
    border: '2px dashed rgba(180, 120, 120, 0.3)',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'all 0.2s ease',
    marginTop: '16px',
    width: '100%',
  },

  dividerOr: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '24px',
  },

  dividerLine: {
    flex: 1,
    height: '1px',
    background: 'rgba(124, 152, 133, 0.2)',
  },

  dividerText: {
    fontSize: '14px',
    color: '#9ab0a5',
    fontWeight: '500',
  },
  
  message: {
    padding: '12px 16px',
    borderRadius: '10px',
    fontSize: '16px',
    textAlign: 'center',
    marginBottom: '16px',
  },
  
  messageError: {
    background: 'rgba(180, 120, 120, 0.12)',
    color: '#8a5a5a',
    border: '1px solid rgba(180, 120, 120, 0.2)',
  },
  
  messageSuccess: {
    background: 'rgba(124, 152, 133, 0.12)',
    color: '#5a7a65',
    border: '1px solid rgba(124, 152, 133, 0.2)',
  },
};

function CreateJobListingPage({ user, setUser }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  
  // Upload state
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDragOver, setIsDragOver] = useState(false);
  const [jobListingPreviewUrl, setJobListingPreviewUrl] = useState(null);
  
  // Modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  
  // Form state
  const [companyName, setCompanyName] = useState('');
  const [roleName, setRoleName] = useState('');
  const [description, setDescription] = useState('');
  const [salaryMin, setSalaryMin] = useState('');
  const [salaryMax, setSalaryMax] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [employmentType, setEmploymentType] = useState('full_time');
  const [jobTag, setJobTag] = useState('');

  // Fetch signed URL for job listing preview
  const fetchJobListingPreviewUrl = async () => {
    if (!user.jobListingFile || !user.jobListingFile.s3Key) return;
    
    try {
      const response = await fetch(`${API_URL}/auth/job-listing/${user._id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setJobListingPreviewUrl(data.data.url);
      }
    } catch (error) {
      console.error('Error fetching job listing URL:', error);
    }
  };

  // Load existing job listing data if available
  useEffect(() => {
    if (user.jobListing) {
      setCompanyName(user.jobListing.companyName || '');
      setRoleName(user.jobListing.roleName || '');
      setDescription(user.jobListing.description || '');
      setSalaryMin(user.jobListing.salaryMin || '');
      setSalaryMax(user.jobListing.salaryMax || '');
      setCurrency(user.jobListing.currency || 'USD');
      setEmploymentType(user.jobListing.employmentType || 'full_time');
      setJobTag(user.jobListing.jobTag || '');
    }
  }, [user.jobListing]);

  // Fetch job listing preview URL when user has a file
  useEffect(() => {
    if (user.jobListingFile && user.jobListingFile.s3Key) {
      fetchJobListingPreviewUrl();
    } else {
      setJobListingPreviewUrl(null);
    }
  }, [user.jobListingFile]);

  // Handle creating new job listing (deleting current one)
  const handleCreateNewJobListing = async () => {
    setShowDeleteModal(false);
    setLoading(true);
    setMessage({ text: '', type: '' });

    try {
      // First delete the job listing file if exists
      if (user.jobListingFile) {
        await fetch(`${API_URL}/auth/delete-job-listing-file`, {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        });
      }

      // Then clear the job listing data
      const response = await fetch(`${API_URL}/auth/update`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ 
          jobListing: {
            companyName: '',
            roleName: '',
            description: '',
            salaryMin: null,
            salaryMax: null,
            currency: 'USD',
            employmentType: 'full_time',
            jobTag: null
          }
        })
      });

      const data = await response.json();

      if (data.success) {
        setUser(data.data.user);
        // Reset form state
        setCompanyName('');
        setRoleName('');
        setDescription('');
        setSalaryMin('');
        setSalaryMax('');
        setCurrency('USD');
        setEmploymentType('full_time');
        setJobTag('');
        setMessage({ text: 'Job listing deleted. You can now create a new one!', type: 'success' });
      } else {
        setMessage({ text: data.message || 'Failed to delete job listing', type: 'error' });
      }
    } catch (error) {
      console.error('Delete error:', error);
      setMessage({ text: 'Failed to delete job listing. Please try again.', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Handle file upload
  const handleFileUpload = async (file) => {
    if (!file) return;

    // Validate file type - PDF only
    if (file.type !== 'application/pdf') {
      setMessage({ text: 'Please upload a PDF file (.pdf)', type: 'error' });
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setMessage({ text: 'File size must be less than 10MB', type: 'error' });
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setMessage({ text: '', type: '' });

    try {
      const formData = new FormData();
      formData.append('jobListingFile', file);

      // Simulate progress for better UX
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + 10;
        });
      }, 100);

      const response = await fetch(`${API_URL}/auth/upload-job-listing`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });

      clearInterval(progressInterval);
      setUploadProgress(100);

      const data = await response.json();

      if (data.success) {
        setUser(data.data.user);
        setMessage({ text: 'Job listing PDF uploaded successfully!', type: 'success' });
        // Fetch the preview URL for the newly uploaded job listing
        if (data.data.user.jobListingFile && data.data.user.jobListingFile.s3Key) {
          try {
            const urlResponse = await fetch(`${API_URL}/auth/job-listing/${data.data.user._id}`, {
              headers: {
                Authorization: `Bearer ${localStorage.getItem('token')}`
              }
            });
            const urlData = await urlResponse.json();
            if (urlData.success) {
              setJobListingPreviewUrl(urlData.data.url);
            }
          } catch (err) {
            console.error('Error fetching job listing preview URL:', err);
          }
        }
      } else {
        setMessage({ text: data.message || 'Failed to upload. Please try again.', type: 'error' });
      }
    } catch (error) {
      console.error('Upload error:', error);
      setMessage({ text: 'Failed to upload. Please try again.', type: 'error' });
    } finally {
      setUploading(false);
      setTimeout(() => setUploadProgress(0), 1000);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    handleFileUpload(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    handleFileUpload(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDeleteJobListingFile = async () => {
    if (!window.confirm('Are you sure you want to delete your uploaded job listing PDF?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/auth/delete-job-listing-file`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      const data = await response.json();

      if (data.success) {
        setUser(data.data.user);
        setMessage({ text: 'Job listing PDF deleted successfully.', type: 'success' });
      } else {
        setMessage({ text: data.message || 'Failed to delete.', type: 'error' });
      }
    } catch (error) {
      console.error('Delete error:', error);
      setMessage({ text: 'Failed to delete. Please try again.', type: 'error' });
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handleSave = async () => {
    if (!jobTag) {
      setMessage({ text: 'Please select a job category', type: 'error' });
      return;
    }

    setLoading(true);
    setMessage({ text: '', type: '' });

    const jobListingData = {
      companyName,
      roleName,
      description,
      salaryMin: salaryMin ? parseInt(salaryMin) : null,
      salaryMax: salaryMax ? parseInt(salaryMax) : null,
      currency,
      employmentType,
      jobTag,
    };

    try {
      const response = await fetch(`${API_URL}/auth/update`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ jobListing: jobListingData, signUpFinished: true })
      });

      const data = await response.json();

      if (data.success) {
        setUser(data.data.user);
        setMessage({ text: 'Job listing saved successfully!', type: 'success' });
        setTimeout(() => navigate('/home'), 1500);
      } else {
        setMessage({ text: data.message, type: 'error' });
      }
    } catch (error) {
      setMessage({ text: 'Unable to save job listing', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const getCurrencySymbol = (code) => {
    const curr = currencies.find(c => c.code === code);
    return curr ? curr.symbol : '$';
  };

  const formatSalary = () => {
    if (!salaryMin && !salaryMax) return null;
    const symbol = getCurrencySymbol(currency);
    if (salaryMin && salaryMax) {
      return `${symbol}${parseInt(salaryMin).toLocaleString()} - ${symbol}${parseInt(salaryMax).toLocaleString()}`;
    } else if (salaryMin) {
      return `From ${symbol}${parseInt(salaryMin).toLocaleString()}`;
    } else {
      return `Up to ${symbol}${parseInt(salaryMax).toLocaleString()}`;
    }
  };

  const getTagInfo = (tagValue) => {
    return jobTags.find(t => t.value === tagValue);
  };

  return (
    <>
      <style>{styles.global}</style>
      
      <div style={styles.container}>
        <div style={styles.bgCircle1}></div>
        <div style={styles.bgCircle2}></div>
        <div style={styles.bgCircle3}></div>
        
        <div style={styles.mainCard}>
          <div style={styles.header}>
            <h1 style={styles.logoText}>Instaview</h1>
            <p style={styles.subtitle}>Create your job listing, {user.firstName}</p>
          </div>

          {message.text && (
            <div style={{
              ...styles.message,
              ...(message.type === 'error' ? styles.messageError : styles.messageSuccess)
            }}>
              {message.text}
            </div>
          )}

          {/* Upload Section */}
          <div 
            style={{
              ...styles.uploadSection,
              ...(isDragOver ? styles.uploadSectionActive : {})
            }}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
          >
            <div style={styles.uploadIcon}>📋</div>
            <div style={styles.uploadTitle}>Upload Job Listing PDF</div>
            <div style={styles.uploadSubtitle}>
              {user.jobListingFile ? 'Replace your current PDF or keep it' : 'Job seekers will be able to view your full job listing when you match'}
            </div>
            
            <label>
              <input
                type="file"
                accept=".pdf"
                onChange={handleFileSelect}
                style={styles.uploadInput}
                disabled={uploading}
              />
              <span style={{
                ...styles.uploadButton,
                opacity: uploading ? 0.7 : 1,
                cursor: uploading ? 'not-allowed' : 'pointer'
              }}>
                {uploading ? '⏳ Uploading...' : user.jobListingFile ? '📁 Replace PDF' : '📁 Choose File'}
              </span>
            </label>

            {uploading && (
              <div style={styles.uploadProgress}>
                <div style={styles.progressBar}>
                  <div style={{...styles.progressFill, width: `${uploadProgress}%`}}></div>
                </div>
                <div style={styles.progressText}>
                  {uploadProgress < 100 ? 'Uploading...' : 'Done!'}
                </div>
              </div>
            )}

            {/* Show uploaded file info and preview */}
            {user.jobListingFile && !uploading && (
              <>
                <div style={styles.uploadedFile}>
                  <div style={styles.uploadedFileIcon}>📕</div>
                  <div style={styles.uploadedFileInfo}>
                    <div style={styles.uploadedFileName}>{user.jobListingFile.originalName}</div>
                    <div style={styles.uploadedFileMeta}>
                      {formatFileSize(user.jobListingFile.size)} • Uploaded {new Date(user.jobListingFile.uploadedAt).toLocaleDateString()}
                    </div>
                  </div>
                  <div style={styles.uploadedFileActions}>
                    <button onClick={handleDeleteJobListingFile} style={styles.deleteButton}>
                      🗑️ Delete
                    </button>
                  </div>
                </div>
                {jobListingPreviewUrl && (
                  <div style={{
                    marginTop: '16px',
                    background: 'white',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    border: '1px solid rgba(124, 152, 133, 0.2)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                  }}>
                    <iframe
                      src={`${jobListingPreviewUrl}#view=FitH&toolbar=0&navpanes=0`}
                      style={{
                        width: '100%',
                        height: '400px',
                        border: 'none',
                        display: 'block',
                      }}
                      title="Job Listing Preview"
                    />
                  </div>
                )}
              </>
            )}

            <div style={styles.uploadFormats}>
              Supported format: PDF (max 10MB)
            </div>
          </div>

          {/* Divider */}
          <div style={styles.dividerOr}>
            <div style={styles.dividerLine}></div>
            <span style={styles.dividerText}>and/or fill in details below</span>
            <div style={styles.dividerLine}></div>
          </div>

          <div style={styles.contentWrapper}>
            {/* Left side - Form */}
            <div style={styles.formSection}>
              <h2 style={styles.sectionTitle}>Job Details</h2>
              
              <div style={styles.formGroup}>
                <label style={styles.label}>Company Name</label>
                <input
                  type="text"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  style={styles.input}
                  placeholder="Acme Corporation"
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Role / Job Title</label>
                <input
                  type="text"
                  value={roleName}
                  onChange={(e) => setRoleName(e.target.value)}
                  style={styles.input}
                  placeholder="Senior Software Engineer"
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Job Description</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value.slice(0, 2000))}
                  style={styles.textarea}
                  placeholder="Describe the role, responsibilities, requirements, and what you're looking for in a candidate..."
                  maxLength={2000}
                />
                <div style={{
                  ...styles.charCount,
                  ...(description.length > 1900 ? styles.charCountWarning : {})
                }}>
                  {description.length}/2000
                </div>
              </div>

              <h3 style={{...styles.sectionTitle, marginTop: '24px'}}>Job Category</h3>
              
              <div style={styles.formGroup}>
                <label style={styles.label}>Select the type of work</label>
                <div style={styles.tagGrid}>
                  {jobTags.map((tag) => (
                    <button
                      key={tag.value}
                      type="button"
                      onClick={() => setJobTag(tag.value)}
                      style={{
                        ...styles.tagButton,
                        ...(jobTag === tag.value ? styles.tagButtonSelected : {})
                      }}
                    >
                      <span style={styles.tagIcon}>{tag.icon}</span>
                      <span style={styles.tagLabel}>{tag.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <h3 style={{...styles.sectionTitle, marginTop: '24px'}}>Compensation</h3>

              <div style={styles.formGroup}>
                <label style={styles.label}>Currency</label>
                <select
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                  style={styles.select}
                >
                  {currencies.map((curr) => (
                    <option key={curr.code} value={curr.code}>
                      {curr.code} ({curr.symbol}) - {curr.name}
                    </option>
                  ))}
                </select>
              </div>

              <div style={styles.row}>
                <div style={styles.halfWidth}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Minimum Salary</label>
                    <input
                      type="number"
                      value={salaryMin}
                      onChange={(e) => setSalaryMin(e.target.value)}
                      style={styles.input}
                      placeholder="50000"
                      min="0"
                    />
                  </div>
                </div>
                <div style={styles.halfWidth}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Maximum Salary</label>
                    <input
                      type="number"
                      value={salaryMax}
                      onChange={(e) => setSalaryMax(e.target.value)}
                      style={styles.input}
                      placeholder="80000"
                      min="0"
                    />
                  </div>
                </div>
              </div>

              <h3 style={{...styles.sectionTitle, marginTop: '24px'}}>Employment Type</h3>

              <div style={styles.formGroup}>
                <div style={styles.radioGroup}>
                  <div style={styles.radioOption}>
                    <input
                      type="radio"
                      name="employmentType"
                      value="full_time"
                      checked={employmentType === 'full_time'}
                      onChange={(e) => setEmploymentType(e.target.value)}
                      style={styles.radioInput}
                    />
                    <div style={{
                      ...styles.radioLabel,
                      ...(employmentType === 'full_time' ? styles.radioLabelSelected : {})
                    }}>
                      <span style={styles.radioIcon}>⏰</span>
                      <span style={styles.radioText}>Full Time</span>
                    </div>
                  </div>
                  <div style={styles.radioOption}>
                    <input
                      type="radio"
                      name="employmentType"
                      value="part_time"
                      checked={employmentType === 'part_time'}
                      onChange={(e) => setEmploymentType(e.target.value)}
                      style={styles.radioInput}
                    />
                    <div style={{
                      ...styles.radioLabel,
                      ...(employmentType === 'part_time' ? styles.radioLabelSelected : {})
                    }}>
                      <span style={styles.radioIcon}>⌛</span>
                      <span style={styles.radioText}>Part Time</span>
                    </div>
                  </div>
                </div>
              </div>

              <div style={styles.buttonContainer}>
                <button
                  onClick={handleSave}
                  disabled={loading}
                  style={{ ...styles.button, ...styles.buttonPrimary }}
                >
                  {loading ? 'Saving...' : 'Save Job Listing'}
                </button>
                {user.signUpFinished && (
                  <button
                    onClick={() => navigate('/home')}
                    style={{ ...styles.button, ...styles.buttonSecondary }}
                  >
                    Back to Home
                  </button>
                )}
              </div>

              {/* Create New Job Listing Button - only show if user has existing job listing */}
              {(user.jobListing?.jobTag || user.jobListingFile) && (
                <button
                  onClick={() => setShowDeleteModal(true)}
                  style={styles.newListingButton}
                >
                  🗑️ Create New Job Listing
                </button>
              )}
            </div>

            {/* Right side - Preview */}
            <div style={styles.previewSection}>
              <h2 style={styles.sectionTitle}>Job Listing Preview</h2>
              
              <div style={styles.jobPreview}>
                <div style={styles.jobHeader}>
                  <div style={styles.jobCompany}>
                    {companyName || <span style={styles.jobEmpty}>Company Name</span>}
                  </div>
                  <div style={styles.jobTitle}>
                    {roleName || <span style={styles.jobEmpty}>Job Title</span>}
                  </div>
                  <div style={styles.jobMeta}>
                    <span style={{
                      ...styles.employmentBadge,
                      ...(employmentType === 'full_time' ? styles.fullTimeBadge : styles.partTimeBadge)
                    }}>
                      {employmentType === 'full_time' ? '🕐 Full Time' : '⌛ Part Time'}
                    </span>
                    {jobTag && (
                      <span style={styles.tagBadge}>
                        {getTagInfo(jobTag)?.icon} {getTagInfo(jobTag)?.label}
                      </span>
                    )}
                    {formatSalary() && (
                      <span style={styles.jobMetaItem}>
                        <span style={styles.jobMetaIcon}>💰</span>
                        {formatSalary()} / year
                      </span>
                    )}
                  </div>
                </div>

                <div style={styles.jobSection}>
                  <div style={styles.jobSectionTitle}>About the Role</div>
                  {description ? (
                    <div style={styles.jobDescription}>{description}</div>
                  ) : (
                    <div style={styles.jobEmpty}>
                      Job description will appear here. Describe the role, responsibilities, 
                      and what you're looking for in a candidate...
                    </div>
                  )}
                </div>

                {(salaryMin || salaryMax) && (
                  <div style={styles.jobSection}>
                    <div style={styles.jobSectionTitle}>Compensation</div>
                    <div style={styles.jobSalary}>{formatSalary()}</div>
                    <div style={{ fontSize: '14px', color: '#6b8080', marginTop: '4px' }}>
                      per year • {currency}
                    </div>
                  </div>
                )}

                <div style={styles.jobSection}>
                  <div style={styles.jobSectionTitle}>Posted By</div>
                  <div style={{ fontSize: '16px', color: '#3d4f4f' }}>
                    {user.firstName} {user.lastName}
                  </div>
                  <div style={{ fontSize: '14px', color: '#6b8080' }}>
                    {user.email}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div style={styles.modalOverlay} onClick={() => setShowDeleteModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalIcon}>⚠️</div>
            <div style={styles.modalTitle}>Create New Job Listing?</div>
            <div style={styles.modalText}>
              Doing this will delete your current job listing. Proceed?
            </div>
            <div style={styles.modalButtons}>
              <button
                onClick={() => setShowDeleteModal(false)}
                style={styles.modalButtonNo}
              >
                No
              </button>
              <button
                onClick={handleCreateNewJobListing}
                style={styles.modalButtonYes}
              >
                Yes
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default CreateJobListingPage;